/**
 * UserProfile.js
 * Active logged-in user details shown in topbar.
 * Click avatar to open dropdown with:
 *  - Username, role, last login
 *  - Session info (token expiry)
 *  - Change password link
 *  - Logout button
 */
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

const ROLE_COLOR = {
  admin:    { bg: '#eff4ff', color: '#1a56db', icon: '👑' },
  operator: { bg: '#fff7ed', color: '#c2410c', icon: '⚙️' },
  viewer:   { bg: '#f0fdf4', color: '#16a34a', icon: '👁' },
};

function fmtDateTime(d) {
  if (!d) return 'Unknown';
  return new Date(d).toLocaleString('en-GB', {
    day:'2-digit', month:'short', year:'numeric',
    hour:'2-digit', minute:'2-digit'
  });
}

export default function UserProfile({ user, onLogout }) {
  const [open,     setOpen]     = useState(false);
  const [showPwd,  setShowPwd]  = useState(false);
  const [curPwd,   setCurPwd]   = useState('');
  const [newPwd,   setNewPwd]   = useState('');
  const [pwdMsg,   setPwdMsg]   = useState('');
  const [saving,   setSaving]   = useState(false);
  const [tokenExp, setTokenExp] = useState(null);
  const ref = useRef(null);

  // Parse token expiry from JWT
  useEffect(() => {
    try {
      const token = localStorage.getItem('adminToken');
      if (token && token.startsWith('ey')) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp) setTokenExp(new Date(payload.exp * 1000));
      }
    } catch {}
  }, []);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function changePassword(e) {
    e.preventDefault();
    if (newPwd.length < 8) { setPwdMsg('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      await api.changePassword(curPwd, newPwd);
      setPwdMsg('✔ Password changed successfully');
      setCurPwd(''); setNewPwd('');
      setTimeout(() => { setPwdMsg(''); setShowPwd(false); }, 3000);
    } catch (err) {
      setPwdMsg('Error: ' + (err.response?.data?.error || err.message));
    } finally { setSaving(false); }
  }

  if (!user) return null;

  const role  = user.role || 'viewer';
  const rc    = ROLE_COLOR[role] || ROLE_COLOR.viewer;
  const initials = (user.username || 'U').slice(0, 2).toUpperCase();

  // Session time remaining
  let sessionInfo = null;
  if (tokenExp) {
    const diff = tokenExp - Date.now();
    if (diff > 0) {
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      sessionInfo = h > 0 ? `${h}h ${m}m remaining` : `${m}m remaining`;
    } else {
      sessionInfo = 'Session expired';
    }
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {/* Avatar button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: open ? '#eff4ff' : 'transparent',
          border: '1px solid ' + (open ? '#bfdbfe' : '#e8ecf4'),
          borderRadius: 8, padding: '5px 10px', cursor: 'pointer',
          transition: 'all 0.15s'
        }}
      >
        {/* Avatar circle */}
        <div style={{
          width: 28, height: 28, borderRadius: '50%',
          background: 'linear-gradient(135deg, #1a56db 0%, #0f2350 100%)',
          color: '#fff', fontSize: 11, fontWeight: 700,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0
        }}>
          {initials}
        </div>
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b', lineHeight: 1.2 }}>
            {user.username}
          </div>
          <div style={{ fontSize: 10, color: rc.color, fontWeight: 600 }}>
            {rc.icon} {role.charAt(0).toUpperCase() + role.slice(1)}
          </div>
        </div>
        <span style={{ fontSize: 10, color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
      </button>

      {/* Dropdown panel */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', borderRadius: 12, minWidth: 280,
          boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e8ecf4',
          zIndex: 500, overflow: 'hidden'
        }}>
          {/* User header */}
          <div style={{ background: 'linear-gradient(135deg, #0f2350 0%, #1a56db 100%)',
            padding: '20px 20px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff', fontSize: 16, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid rgba(255,255,255,0.4)'
              }}>
                {initials}
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>
                  {user.username}
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 12,
                  background: rc.bg, color: rc.color, marginTop: 4, display: 'inline-block'
                }}>
                  {rc.icon} {role.charAt(0).toUpperCase() + role.slice(1)}
                </span>
              </div>
            </div>
          </div>

          {/* Details */}
          <div style={{ padding: '12px 20px' }}>
            {/* Session info */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
                Session
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between',
                fontSize: 12, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f0f4ff' }}>
                <span style={{ color: '#64748b' }}>Status</span>
                <span style={{ color: '#16a34a', fontWeight: 600 }}>● Active</span>
              </div>
              {sessionInfo && (
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: 12, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f0f4ff' }}>
                  <span style={{ color: '#64748b' }}>Session expires</span>
                  <span style={{ fontWeight: 600,
                    color: sessionInfo.includes('expired') ? '#dc2626' : '#1e2a3b' }}>
                    {sessionInfo}
                  </span>
                </div>
              )}
              {tokenExp && (
                <div style={{ display: 'flex', justifyContent: 'space-between',
                  fontSize: 12, color: '#374151', padding: '4px 0', borderBottom: '1px solid #f0f4ff' }}>
                  <span style={{ color: '#64748b' }}>Expires at</span>
                  <span style={{ fontWeight: 500 }}>{fmtDateTime(tokenExp)}</span>
                </div>
              )}
            </div>

            {/* Role permissions */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>
                Permissions
              </div>
              {[
                { perm: 'View Dashboard',      allowed: true },
                { perm: 'Deploy Patches',      allowed: role === 'admin' },
                { perm: 'Emergency Deploy',    allowed: role === 'admin' },
                { perm: 'Manage Users',        allowed: role === 'admin' },
                { perm: 'Submit Approvals',    allowed: role === 'admin' || role === 'operator' },
                { perm: 'Approve Requests',    allowed: role === 'admin' },
              ].map(item => (
                <div key={item.perm} style={{ display: 'flex', justifyContent: 'space-between',
                  alignItems: 'center', padding: '3px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: 11, color: '#64748b' }}>{item.perm}</span>
                  <span style={{ fontSize: 10, fontWeight: 700,
                    color: item.allowed ? '#16a34a' : '#94a3b8' }}>
                    {item.allowed ? '✔' : '—'}
                  </span>
                </div>
              ))}
            </div>

            {/* Change password */}
            {!showPwd ? (
              <button
                onClick={() => setShowPwd(true)}
                style={{ width: '100%', background: '#f8fafc', border: '1px solid #e8ecf4',
                  borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                  color: '#1e2a3b', fontWeight: 600, marginBottom: 8, textAlign: 'left' }}>
                🔑 Change Password
              </button>
            ) : (
              <form onSubmit={changePassword} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#1e2a3b', marginBottom: 6 }}>
                  Change Password
                </div>
                <input
                  type="password" placeholder="Current password"
                  value={curPwd} onChange={e => setCurPwd(e.target.value)}
                  className="form-input" style={{ marginBottom: 6, fontSize: 12 }}
                  required
                />
                <input
                  type="password" placeholder="New password (min 8 chars)"
                  value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  className="form-input" style={{ marginBottom: 6, fontSize: 12 }}
                  minLength={8} required
                />
                {pwdMsg && (
                  <div style={{ fontSize: 11, marginBottom: 6,
                    color: pwdMsg.startsWith('✔') ? '#16a34a' : '#dc2626' }}>
                    {pwdMsg}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="submit" disabled={saving}
                    style={{ flex: 1, background: '#1a56db', color: '#fff', border: 'none',
                      borderRadius: 6, padding: '7px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                    {saving ? '⟳' : 'Update'}
                  </button>
                  <button type="button" onClick={() => { setShowPwd(false); setPwdMsg(''); }}
                    style={{ flex: 1, background: '#f1f5f9', color: '#64748b', border: 'none',
                      borderRadius: 6, padding: '7px', fontSize: 11, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {/* Logout */}
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              style={{ width: '100%', background: '#fee2e2', border: '1px solid #fca5a5',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, cursor: 'pointer',
                color: '#dc2626', fontWeight: 600, textAlign: 'left' }}>
              ⎋ Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
